import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useActionForOrdersMutation,
  useGetOrderQuery,
  useGetTypesOfServicesQuery,
  useSelectWorkerMutation,
  useSetOrderAgreementMutation,
  useUpdateOrderMutation,
} from 'redux/Api/ordersApi/ordersApi';
import { setInfoOrderDetail } from 'redux/ordersDetails/slice';
import { RootState } from 'redux/store';
import { OrderStepsProgress } from '../OrderStepsProgress';
import { OrderStepsActions } from '../OrderStepsActions';
import { OrderHistory } from '../OrderHistory';
import styles from './OrderSteps.module.scss';
import { calculateStepInfo } from './constants';
import { LoadingSkeleton } from 'components/LoadingSkeleton';
import { Box } from '@mantine/core';
import { ModalCancelOrder } from 'components/ModalCancelOrder';
import useYandexMetrica from 'hooks/useYandexMetrica';
import { ValueOrderAgreement } from 'redux/Api/ordersApi/type';
import { SuccessErrorInfo } from 'components/SuccessErrorInfo';
import { useGetInfoUserQuery } from 'redux/Api/profileApi/ProfileApi';
import moment from 'moment';

interface OrderStepsProps {
  saveOrder?: (event: React.MouseEvent<HTMLElement> | any) => void;
  saveDraft?: (event: React.MouseEvent<HTMLElement> | any) => void;
  isLoadingCreateOrder?: boolean;
  setDisableAgreement?: (disable: boolean) => void;
  disableAgreement?: boolean;
  valueOrderAgreement?: ValueOrderAgreement;
  validateConfirmVolume?: () => boolean;
  validateEmployee?: () => boolean;
  setStep?: (step: number) => void;
  disableAgreementButton?: boolean;
  showButtonChat?: boolean;
}

export const OrderSteps: FC<OrderStepsProps> = ({
  saveOrder,
  saveDraft,
  isLoadingCreateOrder,
  setDisableAgreement,
  disableAgreement,
  valueOrderAgreement,
  validateConfirmVolume,
  validateEmployee,
  setStep,
  disableAgreementButton,
  showButtonChat,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: orderData, isLoading } = useGetOrderQuery({ id }, { skip: !id });
  const { data: servicesData } = useGetTypesOfServicesQuery(undefined, { skip: !id });
  const [updateOrder, { isLoading: isUpdateLoading }] = useUpdateOrderMutation();
  const [actionForOrders, { isLoading: isActionLoading }] = useActionForOrdersMutation();
  const [isShowCancelModal, setIsShowCancelModal] = useState(false);
  const { sendGoal } = useYandexMetrica();
  const [setOrderAgreement, { isLoading: isSetOrderAgreementLoading }] =
    useSetOrderAgreementMutation();
  const [selectWorker, { isLoading: isSelectWorkerLoading }] = useSelectWorkerMutation();
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorInfoText, setErrorInfoText] = useState('');
  const { infoOrderDetail } = useSelector((state: RootState) => state.ordersDetailsSlice);

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<string | null>(null);

  const orderCreatedAt = orderData?.order_created_at;

  const [approvalMsLeft, setApprovalMsLeft] = useState(0);

  useEffect(() => {
    if (!orderCreatedAt) {
      setApprovalMsLeft(0);
      return;
    }

    const deadlineMs =
      orderCreatedAt * 1000 +
      parseInt(process.env.REACT_APP_TIMER_FINISH_WORKER_SELECTION || '600000');

    const tick = () => {
      const msLeft = Math.max(0, deadlineMs - Date.now());
      setApprovalMsLeft(msLeft);
      if (msLeft === 0) clearInterval(intervalId);
    };

    const intervalId = setInterval(tick, 1000);
    tick();

    return () => clearInterval(intervalId);
  }, [orderCreatedAt]);

  const approvalTimerLabel = moment.utc(approvalMsLeft).format('mm:ss');

  useEffect(() => {
    if (orderData?.order_data?.order_type && servicesData?.items) {
      const serviceType = servicesData.items.find(
        (service: any) => service.name === orderData.order_data.order_type,
      );
      if (serviceType) {
        dispatch(setInfoOrderDetail({ key: 'order_type_id', value: serviceType.id }));
      }
    }
  }, [orderData?.order_data?.order_type, servicesData?.items, dispatch]);

  const handleSaveOrder = () => {
    saveOrder?.(false);
  };

  const handleUpdateOrder = async (isDraft: boolean) => {
    if (!id) return;

    const {
      date_end,
      date_start,
      with_date_start,
      with_date_end,
      unit_price,
      is_remote,
      use_fast_withdraw,
      ...rest
    } = infoOrderDetail;
    void with_date_start;
    void with_date_end;

    await updateOrder({
      id,
      body: {
        ...rest,
        date_end: date_end
          ? moment(date_end).format('YYYY-MM-DD HH:mm')
          : moment(new Date()).format('YYYY-MM-DD 00:00'),
        date_start: date_start ? moment(date_start).format('YYYY-MM-DD HH:mm') : null,
        unit_price: +unit_price,
        is_remote: is_remote === 'Удаленно',
        is_draft: isDraft,
        use_fast_withdraw: use_fast_withdraw ? 1 : 0,
      },
    });
  };

  const { refetch } = useGetInfoUserQuery();

  const handleAction = async (action: string) => {
    if (!id) return;
    try {
      setCurrentAction(action);
      await actionForOrders({ id, action }).unwrap();
    } catch (error) {
      console.error('Action failed:', error);
      if (
        action === 'finish-worker-selection' ||
        action === 'finish' ||
        action === 'start'
      ) {
        setErrorInfoText(
          (error as { data?: { message?: string } }).data?.message ||
            'Не удалось выполнить действие.',
        );
        setShowErrorNotification(true);
        return;
      }
      throw error;
    } finally {
      setCurrentAction(null);
    }
  };

  const handleConfirmVolume = async () => {
    if (!id) return;

    if (validateConfirmVolume && !validateConfirmVolume()) {
      return;
    }
    if (disableAgreementButton) {
      return;
    }

    setDisableAgreement?.(true);

    try {
      setCurrentAction('confirm_volume');
      await setOrderAgreement({
        id,
        body: valueOrderAgreement as ValueOrderAgreement,
      }).unwrap();
      await refetch();
    } catch (error) {
      console.error('Confirm volume failed:', error);
      setDisableAgreement?.(false);
      setErrorInfoText(
        (error as { data?: { message?: string } }).data?.message ||
          'Ошибка при подтверждении объема',
      );
      setShowErrorNotification(true);
      setTimeout(() => {
        setShowErrorNotification(false);
      }, 3000);
      throw error;
    } finally {
      setCurrentAction(null);
    }
  };

  const handleEditVolume = () => {
    setDisableAgreement?.(false);
  };

  const handleOpenChat = async () => {
    navigate(`/orderchat/chat/new-chat?orderId=${id}`);
  };

  const handleSelectWorker = async () => {
    if (!id) return;

    if (validateEmployee && !validateEmployee()) {
      return;
    }

    if (!infoOrderDetail.worker_id) {
      return;
    }

    try {
      setCurrentAction('select_worker');
      await selectWorker({
        id,
        body: { worker_id: String(infoOrderDetail.worker_id) },
      }).unwrap();
    } catch (error) {
      console.error('Select worker failed:', error);
      setErrorInfoText(
        (error as { data?: { message?: string } }).data?.message ||
          'Ошибка при выборе исполнителя',
      );
      setShowErrorNotification(true);
      setTimeout(() => {
        setShowErrorNotification(false);
      }, 3000);
    } finally {
      setCurrentAction(null);
    }
  };

  const stepInfo = calculateStepInfo(
    orderData?.status?.value,
    orderData?.receipt,
    orderData?.status,
    infoOrderDetail.is_unit_based === 1,
  );
  const {
    step,
    subtitle,
    actions: actionInfo,
    info,
    isError,
    isGray,
    showPrimaryButton,
  } = stepInfo;

  const hasWorkerContacts = !!orderData?.worker?.inn && !!orderData?.worker?.phone;
  const isSelectionTimerActive =
    orderData?.status?.value === 'order_published' && approvalMsLeft > 0;
  const visibleInfo = isSelectionTimerActive ? (
    <p className={styles.approval_timer}>
      Завершить подбор можно через {approvalTimerLabel}
    </p>
  ) : orderData?.status?.value === 'worker_selected_by_platform' && hasWorkerContacts ? (
    false
  ) : (
    info
  );

  useEffect(() => {
    setStep?.(step ?? 0);
  }, [step]);

  useEffect(() => {
    if (showPrimaryButton) setDisableAgreement?.(false);
  }, [showPrimaryButton]);

  const title =
    orderData?.status?.value === 'act_signed_by_customer' &&
    orderData?.receipt?.status === 'error'
      ? orderData?.receipt?.error?.title
      : orderData?.status?.text || 'Создание заказа';

  const handleMenuAction = (actionId: string) => {
    switch (actionId) {
      case 'history':
        setIsHistoryOpen(true);
        break;
      case 'draft':
        saveDraft?.(true);
        break;
      case 'cancel':
        setIsShowCancelModal(true);
        break;
      case 'edit':
        handleEditVolume();
        break;
      case 'create_new':
        navigate('/orders/create');
        break;
      case 'chat':
        handleOpenChat();
        break;
    }
  };

  if (isLoading) {
    return <LoadingSkeleton fullWidth />;
  }

  const isMainButtonLoading = Boolean(
    Array.isArray(actionInfo)
      ? actionInfo.some(
          (a) =>
            (a.action === 'finish_order' && currentAction === 'accept') ||
            (a.action === 'publish' && isUpdateLoading) ||
            (a.action === 'save' && currentAction === 'save'),
        )
      : actionInfo &&
          (((actionInfo as any).action === 'finish_order' &&
            currentAction === 'accept') ||
            ((actionInfo as any).action === 'publish' && isUpdateLoading) ||
            ((actionInfo as any).action === 'save' && currentAction === 'save')),
  );

  const isButtonDisabled = Boolean(currentAction || isUpdateLoading || isActionLoading);

  return (
    <div className={styles.steps_container}>
      <div className={styles.steps}>
        <div className={styles.steps_left}>
          <p className={styles.steps_left__title}>
            {step ? `Шаг ${step} из 7. ` : null} {title}
          </p>
          <p className={styles.steps_left__subtitle}>{subtitle}</p>
          <Box visibleFrom='sm'>
            <OrderStepsProgress step={step} isError={isError} isGray={isGray} />
          </Box>
        </div>

        <OrderStepsActions
          actions={actionInfo as any}
          isUnitBased={infoOrderDetail.is_unit_based}
          info={visibleInfo}
          onMenuAction={handleMenuAction}
          listItems={stepInfo.listItems}
          showButtonChat={showButtonChat}
          loading={
            isMainButtonLoading ||
            isLoadingCreateOrder ||
            isSetOrderAgreementLoading ||
            isSelectWorkerLoading ||
            currentAction === 'start' ||
            currentAction === 'finish' ||
            currentAction === 'finish-worker-selection'
          }
          disabled={isButtonDisabled || isSelectionTimerActive}
          confirmVolume={disableAgreement}
          showPrimaryButton={stepInfo.showPrimaryButton}
          onActionClick={(clickedAction) => {
            if (!clickedAction) return;
            switch (clickedAction) {
              case 'finish_order':
                handleAction('finish');
                break;
              case 'finish-worker-selection':
                handleAction('finish-worker-selection');
                break;
              case 'save':
                handleSaveOrder();
                sendGoal('CreateOrder');
                break;
              case 'publish':
                handleUpdateOrder(false);
                break;
              case 'confirm_volume':
                handleConfirmVolume();
                break;
              case 'select_worker':
                handleSelectWorker();
                break;
              case 'start_order':
                handleAction('start');
                break;
            }
          }}
        />

        {isShowCancelModal && id && (
          <ModalCancelOrder
            isShowCancelModal={isShowCancelModal}
            setIsShowCancelModal={setIsShowCancelModal}
            orderId={id}
            onCancel={async () => {
              await handleAction('cancel');
            }}
          />
        )}

        {isHistoryOpen && (
          <OrderHistory
            history={orderData?.history}
            onClose={() => setIsHistoryOpen(false)}
            assignHistory={orderData?.assign_history}
            showButtons={orderData?.order_data?.is_marketplace === 1}
          />
        )}
      </div>
      <Box hiddenFrom='sm'>
        <OrderStepsProgress step={step} isError={isError} isGray={isGray} />
      </Box>
      {showErrorNotification && (
        <SuccessErrorInfo
          type='error'
          text={errorInfoText}
          handleClose={() => setShowErrorNotification(false)}
        />
      )}
    </div>
  );
};
